import { useState, useEffect, useRef, type ReactNode } from 'react';

import { useLocation } from 'react-router-dom';

import { ProfileCompletenessBanner } from '@/components/auth/ProfileCompletenessBanner';
import { AppFooter } from '@/components/layouts/AppFooter';
import { Header } from '@/components/navigation/Header';
import { Sidebar } from '@/components/navigation/Sidebar';
import { SessionCompanionBar } from '@/components/sessions/SessionCompanionBar';
import { useSessionsStore } from '@/stores/store';

interface LayoutShellProps {
  children: ReactNode;
  /** Hide sidebar completely — no sidebar, no menu button */
  hideSidebar?: boolean;
  /** Remove default content padding (e.g. for full-bleed landing pages) */
  noPadding?: boolean;
  /**
   * Optional backdrop (e.g. the spinning-globe MarketplaceBackgroundVideo)
   * rendered as a flex SIBLING of the scroller inside the content region,
   * between the header/banner and the footer. Because its size comes from the
   * flex layout of the visible content REGION (the viewport minus
   * sidebar/header/banner) rather than from the scrollable content height, an
   * `object-cover` video no longer re-crops/restretches as page content grows
   * (GW-10550) — it stays fixed to the visible region while page content
   * scrolls over it (lifted via `relative z-10`), and it never covers the
   * sidebar, header, or footer. Non-globe consumers omit this prop and render
   * the scroller unchanged (no wrapper, no z-index).
   */
  background?: ReactNode;
}

/**
 * LayoutShell - Shared layout structure for Sidebar + Header + content area.
 *
 * PRIVATE composition primitive of AppLayout (GW-10533): AppLayout.tsx is the
 * ONLY module that imports it — the `default` variant and the `showGlobeBackground`
 * globe host compose it internally. Routes/pages must render `<AppLayout>`, never
 * LayoutShell directly.
 *
 * hideSidebar: removes sidebar entirely (home page hero)
 * noPadding: removes default p-6 from content area
 * background: optional viewport-region backdrop rendered behind scrolling content
 *
 * GW-10550: owns the shared pathname scroll-to-top reset. The `main#main-content`
 * scroller persists across navigations (every authenticated page mounts under one
 * shared AppLayout), so without this reset, navigating from a scrolled long page
 * lands mid-page. Reset lives here (not per-page or in the globe host) so ALL
 * authenticated navigation resets scroll to top.
 */
export function LayoutShell({ children, hideSidebar, noPadding, background }: LayoutShellProps) {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth >= 1024;
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar-collapsed');
      // GW-2083: Default to collapsed (true) for unified landing page layout
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const { activeLaunchedSession, appTabRef, clearActiveLaunchedSession } = useSessionsStore();
  const isTerminalSession = activeLaunchedSession?.accessMode === 'TERMINAL';

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // GW-10550: the #main-content scroller persists across route changes (this
  // shell is mounted at a shared route ancestor), so it retains its scroll
  // offset. Reset it to the top on every pathname change so navigating from a
  // scrolled long page lands at the top. scrollTo is unimplemented in jsdom;
  // guard so tests don't throw.
  useEffect(() => {
    const scroller = document.getElementById('main-content');
    scroller?.scrollTo?.({ top: 0 });
  }, [pathname]);

  // GW-9546: Re-sync sidebar open/closed state when window is resized across the lg (1024px) breakpoint.
  // Root cause #1: sidebarOpen was set once in the lazy useState initializer above and never updated on resize.
  const wasDesktopRef = useRef(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;
      const wasDesktop = wasDesktopRef.current;

      // Only re-sync when crossing the breakpoint (not for in-breakpoint resizes)
      if (isDesktop !== wasDesktop) {
        setSidebarOpen(isDesktop);
        wasDesktopRef.current = isDesktop;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GW-10609: single <main> scroller markup for both the plain and backgrounded
  // shells. The scroller is ALWAYS `relative` (GW-12084): it must be the
  // containing block for its own position:absolute descendants (e.g. sr-only
  // spans deep in streaming agent cards) so they cannot escape to the document
  // and inflate document.scrollHeight into a rogue window scroll. When a
  // `background` is present the scroller is additionally lifted above the
  // backdrop with `z-10` (the content-lift contract moved here from the page
  // consumers) and the footer carries an opaque `bg-background` so the globe
  // never bleeds through it; without one it renders with no z-index and a plain
  // footer. The scroller markup itself lives once.
  const scroller = (
    <main
      id="main-content"
      className={`relative flex min-w-0 flex-1 [scrollbar-gutter:stable] flex-col overflow-x-clip overflow-y-auto${
        background ? ' z-10' : ''
      }`}
    >
      <div data-testid="main-content-inner" className={`min-w-0 flex-1 ${noPadding ? '' : 'p-6'}`}>
        {children}
      </div>
      <AppFooter className={background ? 'bg-background border-t px-6 py-4' : undefined} />
    </main>
  );

  return (
    <div className="bg-background flex h-screen overflow-hidden">
      {!hideSidebar && (
        <Sidebar
          isOpen={sidebarOpen}
          isCollapsed={sidebarCollapsed}
          onClose={() => setSidebarOpen(false)}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Header onMenuClick={hideSidebar ? undefined : () => setSidebarOpen(!sidebarOpen)} />
        {/* GW-2325: Profile completeness banner appears on all authenticated pages */}
        <ProfileCompletenessBanner />
        {background ? (
          // GW-10550: the backdrop is a flex SIBLING of the scroller, filling the
          // visible content REGION (viewport minus sidebar/header/banner) via
          // `flex-1`. Its size therefore comes from the layout, not the scrollable
          // content height, so an `object-cover` globe no longer restretches as
          // page content grows. The scroller (lifted via `relative z-10`) is its
          // sibling inside this region wrapper.
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
            {background}
            {scroller}
          </div>
        ) : (
          scroller
        )}
        {activeLaunchedSession && isTerminalSession && (
          <SessionCompanionBar
            session={activeLaunchedSession}
            appTabRef={appTabRef}
            onSessionEnd={clearActiveLaunchedSession}
          />
        )}
      </div>
    </div>
  );
}
